import { Product } from "@sections/product";
import { sendAddToCart, fetchCartData } from "../helpers/cart";

class ProductSwatches extends HTMLElement {
  private _productComponent: Product | null = null;
  private _variantSelectors: NodeListOf<HTMLInputElement> | null = null;
  private _selectedVariantInput: HTMLInputElement | null = null;
  private soldOutToggles: HTMLElement[] | null = null;
  private atcButton: HTMLElement | null = null;
  private soldOutButton: HTMLElement | null = null;
  private bisButton: HTMLElement | null = null;
  private bisPopup: HTMLElement | null = null;
  private maxQuantityButton: HTMLElement | null = null;

  constructor() {
    super();
  }

  static get observedAttributes() {
    return ["product"];
  }

  connectedCallback() {
    this.cacheHTMLElements();
    this.attachEventListeners();
    this.checkMaxQuantityOnLoad();
  }

  private cacheHTMLElements() {
    this._productComponent = document.querySelector<Product>("uwp-product");
    this._selectedVariantInput = this.querySelector<HTMLInputElement>(
      "[data-variant-input]",
    );

    if (window.themeVars.booleans.product.beer_box === false) {
      this._variantSelectors = this.querySelectorAll<HTMLInputElement>(
        "[data-variant-select]",
      );
    }

    this.soldOutToggles = [
      ...this.querySelectorAll("[data-sold-out-toggle"),
    ] as HTMLElement[];

    this.atcButton =
      (this._productComponent?.querySelector("#atcButton") as HTMLElement) ||
      (this._productComponent?.querySelector(
        "#multipleAtcButton",
      ) as HTMLElement);

    this.soldOutButton = this._productComponent?.querySelector(
      "[data-sold-out-button]",
    ) as HTMLElement;

    this.bisButton = this._productComponent?.querySelector(
      "[data-bis-trigger]",
    ) as HTMLElement;

    this.bisPopup = document.querySelector(
      `uwp-product-notify-me[data-product-id="${this.bisButton?.getAttribute("data-product")}"]`,
    );

    // Create max quantity button if it doesn't exist
    if (!this._productComponent?.querySelector("[data-max-quantity-button]")) {
      const maxBtn = document.createElement("button") as HTMLButtonElement;
      maxBtn.type = "button";
      maxBtn.className =
        "product__preorder-button product__max-quantity-button btn btn-white";
      maxBtn.setAttribute("data-max-quantity-button", "");
      maxBtn.disabled = true;
      maxBtn.style.border = "1px solid #000";
      maxBtn.style.color = "#000";
      maxBtn.innerHTML = "<span>You've reached the max quantity</span>";
      this.maxQuantityButton = maxBtn;

      // Insert after the ATC button
      const productButtonWrapper = this._productComponent?.querySelector(
        ".product__button__wrapper",
      );
      if (productButtonWrapper && this.atcButton) {
        productButtonWrapper.appendChild(this.maxQuantityButton);
      }
    } else {
      this.maxQuantityButton = this._productComponent?.querySelector(
        "[data-max-quantity-button]",
      ) as HTMLElement;

      // Make sure it has the right styling
      if (this.maxQuantityButton) {
        this.maxQuantityButton.style.border = "1px solid #000";
        this.maxQuantityButton.style.color = "#000";
      }
    }
  }

  private attachEventListeners() {
    const atcButtons = document.querySelectorAll("#atcButton");
    atcButtons.forEach((button) => {
      button.addEventListener("click", this.handleAtcButtonClick.bind(this));
    });

    if (this._variantSelectors) {
      this.selectVariant(Array.from(this._variantSelectors));
    }

    this.soldOutToggles?.forEach((toggle) => {
      toggle.addEventListener("click", this.handleSoldOutToggle.bind(this));
    });

    this._productComponent?.addEventListener(
      "quantityChanged",
      this.showATC.bind(this),
    );

    document
      .querySelectorAll(".product-swatches__quantity")
      .forEach((quantitySelector) => {
        quantitySelector.addEventListener("click", () => {
          if (
            quantitySelector.classList.contains(
              "product-swatches__form-group--sold-out",
            ) === false
          ) {
            this.showATC();
          }
        });
      });

    if (!this._productComponent) return;

    this.bisButton?.addEventListener("click", () => {
      this.bisPopup?.setAttribute("is-visible", "true");
    });

    // Listen for quantity changes to check max quantity
    document.querySelectorAll("[data-qty-input]").forEach((input) => {
      input.addEventListener("change", (event) => {
        // Skip if this is the initial setup
        if ((event as CustomEvent).type === "initialLoad") return;
        this.checkMaxQuantity();
      });
    });
  }

  private async checkMaxQuantity(shouldResetInputs = true) {
    // Get all quantity inputs
    const inputs =
      document.querySelectorAll<HTMLInputElement>("[data-qty-input]");
    let allMaxedOut = true;

    // Check each input to see if any can be incremented
    for (const input of inputs) {
      const max = parseInt(input.max, 10);
      const variantId = input.getAttribute("data-id");

      if (!variantId) continue;

      // If max is 0, nothing can be added to cart
      if (max <= 0 && shouldResetInputs) {
        input.value = "0";
      }

      // If max is greater than 0, we can add to cart
      if (max > 0) {
        allMaxedOut = false;
      }
    }

    // If all variants are at max quantity, show max quantity button instead of ATC
    if (allMaxedOut && inputs.length > 0) {
      this.atcButton?.classList.add("hidden");
      this.maxQuantityButton?.classList.remove("hidden");
    } else {
      this.maxQuantityButton?.classList.add("hidden");
    }
  }

  private showATC(): void {
    const product = JSON.parse(
      this._productComponent?.getAttribute("product") || "{}",
    );

    const selectedSoldOut = this.soldOutToggles?.find((toggle) =>
      toggle.classList.contains("is-selected"),
    );
    selectedSoldOut?.classList.remove("is-selected");
    if (!product.available) {
      return;
    }

    // Check if max quantity is reached before showing ATC, but don't reset inputs
    this.checkMaxQuantity(false);

    if (!this.maxQuantityButton?.classList.contains("hidden")) {
      return; // Don't show ATC if max quantity is reached
    }

    this.atcButton?.classList.remove("hidden");
    [this.soldOutButton, this.bisButton].forEach((button) =>
      button?.classList.add("hidden"),
    );
  }

  private handleSoldOutToggle(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const target =
      (event.target as HTMLElement).closest("[data-sold-out-toggle]") ||
      (event.target as HTMLElement);

    this.soldOutToggles?.forEach((toggle) => {
      if (toggle === target && toggle.classList.contains("is-selected")) {
        toggle.classList.remove("is-selected");
        return;
      }
      toggle.classList.toggle("is-selected", toggle === target);
    });

    const isSelected = target.classList.contains("is-selected");

    if (!isSelected) {
      this.showATC();
      return;
    }

    const bisEnabled = target.hasAttribute("data-show-bis");
    this.atcButton?.classList.toggle("hidden", isSelected);
    this.maxQuantityButton?.classList.add("hidden"); // Hide max quantity button when variant is sold out
    this.soldOutButton?.classList.toggle("hidden", isSelected && bisEnabled);
    if (!this.bisButton || !this.bisPopup) {
      return;
    }

    this.bisPopup?.setAttribute(
      "variant-id",
      target.getAttribute("data-variant-id") || "",
    );
    this.bisButton?.classList.toggle("hidden", isSelected && !bisEnabled);
  }

  private async handleAtcButtonClick(): Promise<void> {
    // Check cart quantities before adding to cart
    const cartData = await fetchCartData();

    const productForm = this.closest(".ProductForm") as HTMLFormElement;
    if (!productForm) {
      return;
    }

    // For single variant products
    const variantInput = productForm?.querySelector<HTMLInputElement>(
      '.product-swatches__form-group--variant-select.selected [name="product_variant"]',
    );

    if (variantInput) {
      const variantId = variantInput.value;
      const cartItem = cartData.items.find(
        (item) => item.variant_id.toString() === variantId,
      );

      // Check max quantity for this variant
      const maxQuantity = parseInt(
        variantInput.getAttribute("data-max-quantity") || "99",
        10,
      );
      const cartQuantity = cartItem ? cartItem.quantity : 0;

      if (cartQuantity >= maxQuantity) {
        // Show error message
        const errorMessage = document.createElement("span");
        errorMessage.classList.add("product-swatches__max-qty-error");
        errorMessage.innerHTML = "You've reached the max quantity";

        // Style message for visibility
        errorMessage.style.display = "block";
        errorMessage.style.color = "red";
        errorMessage.style.fontWeight = "bold";
        errorMessage.style.marginTop = "10px";
        errorMessage.style.marginBottom = "10px";
        errorMessage.style.position = "relative";
        errorMessage.style.zIndex = "10";

        // Find the swatches container rather than button wrapper for better placement
        const swatchesContainer = productForm.querySelector(
          ".product-swatches__form-group-wrapper",
        );
        if (swatchesContainer) {
          // Remove any existing error messages
          const existingError = productForm.querySelector(
            ".product-swatches__max-qty-error",
          );
          if (existingError) {
            existingError.remove();
          }

          swatchesContainer.insertAdjacentElement("afterend", errorMessage);

          setTimeout(() => {
            errorMessage.remove();
          }, 3000);
        }

        return; // Prevent adding to cart
      }

      const item = {
        id: variantId || "",
        quantity: 1,
        properties: {} as Record<string, string>,
        selling_plan: "",
      };

      const pfandValue = variantInput.getAttribute("data-pfand");

      if (pfandValue) {
        item.properties["Pfand"] = pfandValue;
      }

      sendAddToCart([item]);
      return;
    }

    // For multi-variant products (beer box)
    const inputs =
      productForm.querySelectorAll<HTMLInputElement>("[data-qty-input]");
    const items = [];
    let exceedsMaxQuantity = false;

    for (const input of inputs) {
      const quantity = parseInt(input.value, 10);
      if (quantity <= 0) continue;

      const variantId = input.getAttribute("data-id");
      if (!variantId) continue;

      const cartItem = cartData.items.find(
        (item) => item.variant_id.toString() === variantId,
      );

      // Get actual max per product
      const originalMax = parseInt(input.max, 10);
      const cartQuantity = cartItem ? cartItem.quantity : 0;
      const totalQuantity = cartQuantity + quantity;

      // Check if adding this quantity would exceed the max allowed
      if (totalQuantity > originalMax) {
        exceedsMaxQuantity = true;
        break;
      }

      const pfandValue = input.getAttribute("data-pfand");
      const properties = {} as Record<string, string>;

      if (pfandValue) {
        properties["Pfand"] = pfandValue;
      }

      items.push({
        id: variantId,
        quantity,
        properties,
        selling_plan: "",
      });
    }

    if (exceedsMaxQuantity) {
      // Show error message
      const errorMessage = document.createElement("span");
      errorMessage.classList.add("product-swatches__max-qty-error");
      errorMessage.innerHTML = "You've reached the max quantity";

      // Style message for visibility
      errorMessage.style.display = "block";
      errorMessage.style.color = "red";
      errorMessage.style.fontWeight = "bold";
      errorMessage.style.marginTop = "10px";
      errorMessage.style.marginBottom = "10px";
      errorMessage.style.position = "relative";
      errorMessage.style.zIndex = "10";

      // Find the swatches container rather than button wrapper for better placement
      const swatchesContainer = productForm.querySelector(
        ".product-swatches__form-group-wrapper",
      );
      if (swatchesContainer) {
        // Remove any existing error messages
        const existingError = productForm.querySelector(
          ".product-swatches__max-qty-error",
        );
        if (existingError) {
          existingError.remove();
        }

        swatchesContainer.insertAdjacentElement("afterend", errorMessage);

        setTimeout(() => {
          errorMessage.remove();
        }, 3000);
      }

      return; // Prevent adding to cart
    }

    if (items.length > 0) {
      sendAddToCart(items);
    }
  }

  private selectVariant(variantSelectors: HTMLInputElement[]) {
    const atcButtonTotal = document.querySelector("[data-product-total]");
    variantSelectors.forEach((selector) => {
      const price = selector.querySelector("[data-price]");
      selector.addEventListener("click", () => {
        variantSelectors.forEach((s) => s.classList.remove("selected"));
        selector.classList.add("selected");
        if (this._selectedVariantInput) {
          const variantId = selector.getAttribute("data-variant-id") || "";
          this._selectedVariantInput.value = variantId;
          if (atcButtonTotal) {
            atcButtonTotal.innerHTML = `<span class="atc-breaker">|</span>${price?.innerHTML || ""}`;
          }
        }
      });
    });
  }

  // New specialized method for initial load that only sets values to 0 when needed
  private async checkMaxQuantityOnLoad() {
    // Get all quantity inputs
    const inputs =
      document.querySelectorAll<HTMLInputElement>("[data-qty-input]");

    // Only set to 0 if max quantity is 0 (nothing left to add)
    for (const input of inputs) {
      const max = parseInt(input.max, 10);
      const variantId = input.getAttribute("data-id");

      if (!variantId) continue;

      // If max is already 0, nothing can be added to cart
      if (max <= 0) {
        input.value = "0";
      }
      // Otherwise leave the default min value from the template
    }

    // Now call the regular function without resetting inputs
    this.checkMaxQuantity(false);
  }
}

customElements.define("uwp-product-swatches", ProductSwatches);
